import { useState, useEffect, useCallback } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg from '@/components/base/SchemaOrg';
import { heroSlides, comics, categories, articles } from '@/mocks/manga';

export default function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = useCallback(() => {
    setCurrentSlide(prev => (prev + 1) % heroSlides.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide(prev => (prev - 1 + heroSlides.length) % heroSlides.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  const hotComics = [...comics].sort((a, b) => b.views - a.views).slice(0, 8);
  const latestComics = [...comics].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()).slice(0, 8);
  const topRated = [...comics].sort((a, b) => b.rating - a.rating).slice(0, 5);

  const homeArticles = articles.slice(0, 4);

  return (
    <>
      <SchemaOrg type="WebPage" data={{
        name: '51漫画网 - 首页',
        description: '51漫画网提供官方安全入口，涵盖51漫画网页版、51漫画网址及51漫画官网版，是漫迷首选的在线阅读平台，快速访问51漫画主页，开启高清漫画之旅。',
        url: 'https://www.51manhua.com',
        isPartOf: { '@type': 'WebSite', name: '51漫画网', url: 'https://www.51manhua.com' },
      }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Hero Carousel */}
        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
          {heroSlides.map((slide, idx) => (
            <div
              key={slide.id}
              className={`absolute inset-0 transition-opacity duration-700 ${
                idx === currentSlide ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
            >
              <img
                src={slide.image}
                alt={slide.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/50"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
                <div className="max-w-7xl mx-auto">
                  <h2 className="text-2xl md:text-4xl font-heading text-white font-bold mb-2">{slide.title}</h2>
                  <p className="text-sm md:text-lg text-white/80 mb-4">{slide.subtitle}</p>
                  <Link
                    to={`/article/${slide.comicId}`}
                    className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    立即阅读 <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
            </div>
          ))}

          <button
            onClick={prevSlide}
            className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-50/30 backdrop-blur-sm text-white hover:bg-background-50/50 transition-colors cursor-pointer z-10"
            aria-label="上一张"
          >
            <i className="ri-arrow-left-s-line text-lg md:text-xl"></i>
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-50/30 backdrop-blur-sm text-white hover:bg-background-50/50 transition-colors cursor-pointer z-10"
            aria-label="下一张"
          >
            <i className="ri-arrow-right-s-line text-lg md:text-xl"></i>
          </button>

          <div className="absolute bottom-4 md:bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-10">
            {heroSlides.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentSlide(idx)}
                className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
                  idx === currentSlide ? 'w-6 bg-white' : 'bg-white/50 hover:bg-white/80'
                }`}
                aria-label={`切换到第 ${idx + 1} 张`}
              />
            ))}
          </div>
        </section>

        {/* Category Navigation */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10">
          <div className="flex items-center gap-3 overflow-x-auto pb-2">
            {categories.map(cat => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-600 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer flex-shrink-0"
              >
                <i className={`${cat.icon} text-base`}></i>
                {cat.name}
              </Link>
            ))}
          </div>
        </section>

        {/* Hot Comics */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 pb-10">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-xl md:text-2xl font-heading text-foreground-950 font-bold">热门推荐</h2>
              <p className="text-sm text-foreground-500 mt-1">最受漫迷欢迎的精彩漫画</p>
            </div>
            <Link
              to="/category/all?sort=popular"
              className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap font-medium"
            >
              查看更多 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
            {hotComics.map(comic => (
              <ComicCard key={comic.id} {...comic} />
            ))}
          </div>
        </section>

        {/* Latest Updates */}
        <section className="bg-background-100 py-10">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-xl md:text-2xl font-heading text-foreground-950 font-bold">最新更新</h2>
                <p className="text-sm text-foreground-500 mt-1">每日更新，精彩不断</p>
              </div>
              <Link
                to="/category/all?sort=latest"
                className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap font-medium"
              >
                查看更多 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
              {latestComics.map(comic => (
                <ComicCard key={comic.id} {...comic} />
              ))}
            </div>
          </div>
        </section>

        {/* Top Rated + Articles */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-10">
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="lg:w-72 flex-shrink-0">
              <div className="bg-background-100 rounded-lg p-5 sticky top-20">
                <h2 className="text-lg font-heading text-foreground-950 font-bold mb-4 flex items-center gap-2">
                  <i className="ri-trophy-line text-accent-500"></i>
                  评分排行榜
                </h2>
                <div className="flex flex-col gap-1">
                  {topRated.map((comic, idx) => (
                    <Link
                      key={comic.id}
                      to={`/article/${comic.id}`}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-background-200 transition-colors cursor-pointer group"
                    >
                      <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold flex-shrink-0 ${
                        idx === 0 ? 'bg-accent-500 text-background-50' :
                        idx === 1 ? 'bg-secondary-500 text-background-50' :
                        idx === 2 ? 'bg-primary-500 text-background-50' :
                        'bg-background-300 text-foreground-600'
                      }`}>
                        {idx + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors truncate">{comic.title}</p>
                        <p className="text-xs text-foreground-500">{comic.author}</p>
                      </div>
                      <span className="text-xs font-semibold text-accent-500 flex-shrink-0">{comic.rating}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex-1">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h2 className="text-xl md:text-2xl font-heading text-foreground-950 font-bold">漫画资讯</h2>
                  <p className="text-sm text-foreground-500 mt-1">最新漫画资讯与深度解读</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {homeArticles.map(article => {
                  const catInfo = categories.find(c => c.id === article.category);
                  return (
                    <Link
                      key={article.id}
                      to={`/article/${article.id}`}
                      className="group bg-background-50 rounded-lg overflow-hidden hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <div className="aspect-[16/9] overflow-hidden bg-background-200">
                        <img
                          src={article.cover}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                        />
                      </div>
                      <div className="p-4">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          {catInfo && (
                            <span className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">
                              {catInfo.name}
                            </span>
                          )}
                          <span className="text-xs text-foreground-500">{article.date}</span>
                        </div>
                        <h3 className="text-base font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 mb-2">{article.title}</h3>
                        <p className="text-sm text-foreground-600 line-clamp-2">{article.summary}</p>
                        <div className="flex items-center gap-1 mt-3 text-xs text-foreground-500">
                          <i className="ri-eye-line"></i>
                          <span>{article.views.toLocaleString()} 次阅读</span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Stats Banner */}
        <section className="bg-primary-500 py-12">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div>
                <p className="text-3xl md:text-4xl font-heading font-bold text-background-50">10,000+</p>
                <p className="text-sm text-background-50/80 mt-1">漫画资源</p>
              </div>
              <div>
                <p className="text-3xl md:text-4xl font-heading font-bold text-background-50">500万+</p>
                <p className="text-sm text-background-50/80 mt-1">注册用户</p>
              </div>
              <div>
                <p className="text-3xl md:text-4xl font-heading font-bold text-background-50">99.9%</p>
                <p className="text-sm text-background-50/80 mt-1">服务可用率</p>
              </div>
              <div>
                <p className="text-3xl md:text-4xl font-heading font-bold text-background-50">8年</p>
                <p className="text-sm text-background-50/80 mt-1">稳定运营</p>
              </div>
            </div>
          </div>
        </section>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}